import { useCallback, useEffect } from "react";
import { CreativeReportData } from "../types";
import { CreativeReportMetadata, CreativeSectionConfiguration } from "@/types";

const CACHE_PREFIX = "creative_report_cache_";
const MAX_CACHE_ENTRIES = 100; // Maximum number of sections to keep in cache

interface CacheEntry {
  data: CreativeReportData;
  timestamp: number;
  configuration: CreativeSectionConfiguration;
  selectedDate: CreativeReportMetadata;
}

interface IndexedDBCacheEntry extends CacheEntry {
  id: string; // Cache key as the primary key
}

class IndexedDBCache {
  private dbName = "CreativeReportCache";
  private version = 1;
  private storeName = "cacheEntries";
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () =>
        reject(request.error || new Error("IndexedDB operation failed"));
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: "id" });
          store.createIndex("timestamp", "timestamp", { unique: false });
        }
      };
    });
  }

  async get(key: string): Promise<CacheEntry | null> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.get(key);

      request.onerror = () =>
        reject(request.error || new Error("IndexedDB operation failed"));
      request.onsuccess = () => {
        const entry = request.result as IndexedDBCacheEntry | undefined;
        if (!entry) {
          resolve(null);
          return;
        }

        resolve({
          data: entry.data,
          timestamp: entry.timestamp,
          configuration: entry.configuration,
          selectedDate: entry.selectedDate,
        });
      };
    });
  }

  async set(key: string, entry: CacheEntry): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.put({
        id: key,
        ...entry,
      });

      request.onerror = () =>
        reject(request.error || new Error("IndexedDB operation failed"));
      request.onsuccess = () => resolve();
    });
  }

  async delete(key: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(key);

      request.onerror = () =>
        reject(request.error || new Error("IndexedDB operation failed"));
      request.onsuccess = () => resolve();
    });
  }

  async clearForReport(accountId: string, reportId: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);

      // Use cursor to iterate through entries without loading all into memory
      const cursorRequest = store.openCursor();
      const prefix = `${CACHE_PREFIX}${accountId}_${reportId}_`;

      cursorRequest.onerror = () =>
        reject(cursorRequest.error || new Error("IndexedDB operation failed"));
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;

        if (cursor) {
          const key = cursor.key as string;

          if (key.startsWith(prefix)) {
            cursor.delete();
          }

          cursor.continue();
        } else {
          // Cursor finished
          resolve();
        }
      };
    });
  }

  async clearForAccount(accountId: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);

      // Use cursor to iterate through entries without loading all into memory
      const cursorRequest = store.openCursor();
      const prefix = `${CACHE_PREFIX}${accountId}_`;

      cursorRequest.onerror = () =>
        reject(cursorRequest.error || new Error("IndexedDB operation failed"));
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;

        if (cursor) {
          const key = cursor.key as string;

          if (key.startsWith(prefix)) {
            cursor.delete();
          }

          cursor.continue();
        } else {
          // Cursor finished
          resolve();
        }
      };
    });
  }

  async clearByConfiguration(
    accountId: string,
    configMatcher: (config: CreativeSectionConfiguration) => boolean,
  ): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);

      // Use cursor to iterate through entries without loading all into memory
      const cursorRequest = store.openCursor();
      const prefix = `${CACHE_PREFIX}${accountId}_`;

      cursorRequest.onerror = () =>
        reject(cursorRequest.error || new Error("IndexedDB operation failed"));
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;

        if (cursor) {
          const key = cursor.key as string;
          const value = cursor.value as CacheEntry;

          // Check if this entry is for the correct account and matches the configuration
          if (
            key.startsWith(prefix) &&
            value.configuration &&
            configMatcher(value.configuration)
          ) {
            cursor.delete();
          }

          cursor.continue();
        } else {
          // Cursor finished
          resolve();
        }
      };
    });
  }

  async clearAll(): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();

      request.onerror = () =>
        reject(request.error || new Error("IndexedDB operation failed"));
      request.onsuccess = () => resolve();
    });
  }

  async getCount(): Promise<number> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.count();

      request.onerror = () =>
        reject(request.error || new Error("IndexedDB operation failed"));
      request.onsuccess = () => resolve(request.result);
    });
  }

  async removeOldestEntries(): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const index = store.index("timestamp");

      // Get count first
      const countRequest = store.count();
      countRequest.onerror = () =>
        reject(countRequest.error || new Error("IndexedDB operation failed"));
      countRequest.onsuccess = () => {
        const totalCount = countRequest.result;

        if (totalCount <= MAX_CACHE_ENTRIES) {
          resolve();
          return;
        }

        // Calculate how many entries to delete
        const entriesToDelete = totalCount - MAX_CACHE_ENTRIES;

        // Use cursor to iterate through oldest entries
        const cursorRequest = index.openCursor();
        let deletedCount = 0;

        cursorRequest.onerror = () =>
          reject(cursorRequest.error || new Error("IndexedDB operation failed"));
        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;

          if (cursor && deletedCount < entriesToDelete) {
            cursor.delete();
            deletedCount++;
            cursor.continue();
          } else {
            resolve();
          }
        };
      };
    });
  }

  async updateTimestamp(key: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const getRequest = store.get(key);

      getRequest.onerror = () =>
        reject(getRequest.error || new Error("IndexedDB operation failed"));
      getRequest.onsuccess = () => {
        const entry = getRequest.result as IndexedDBCacheEntry | undefined;
        if (entry) {
          entry.timestamp = Date.now();
          const putRequest = store.put(entry);
          putRequest.onerror = () =>
            reject(putRequest.error || new Error("IndexedDB operation failed"));
          putRequest.onsuccess = () => resolve();
        } else {
          resolve();
        }
      };
    });
  }
}

const indexedDBCache = new IndexedDBCache();

export const useReportCache = () => {
  // Initialize IndexedDB on mount
  useEffect(() => {
    indexedDBCache.init().catch(console.error);
  }, []);

  // No longer clearing cache on browser refresh - maintaining 50 sections instead

  const getCacheKey = useCallback(
    (
      accountId: string,
      reportId: string,
      sectionId: string,
      configuration: CreativeSectionConfiguration,
      reportData?: CreativeReportMetadata,
    ) => {
      const date = configuration.date;
      const dateKey = date ? `${date.start_date}_${date.end_date}` : "no_date";

      // Include report_filters in cache key if reportData is provided
      const filtersKey = reportData?.report_filters
        ? `_${JSON.stringify(reportData.report_filters)}`
        : "";

      return `${CACHE_PREFIX}${accountId}_${reportId}_${sectionId}_${dateKey}${filtersKey}`;
    },
    [],
  );

  const checkCache = useCallback(
    async (
      accountId: string,
      reportId: string,
      sectionId: string,
      sectionConfiguration: CreativeSectionConfiguration,
      reportData?: CreativeReportMetadata,
    ): Promise<CreativeReportData | null> => {
      const cacheKey = getCacheKey(
        accountId,
        reportId,
        sectionId,
        sectionConfiguration,
        reportData,
      );

      try {
        const cachedData = await indexedDBCache.get(cacheKey);

        if (cachedData) {
          const {
            data,
            configuration: cachedConfig,
            selectedDate: cachedReportData,
          } = cachedData;

          const configMatches =
            JSON.stringify(cachedConfig) === JSON.stringify(sectionConfiguration);

          let reportDataMatches = true;
          if (reportData) {
            // Compare reportData fields (date, compare_date, report_filters)
            reportDataMatches =
              JSON.stringify(cachedReportData?.date) ===
                JSON.stringify(reportData.date) &&
              JSON.stringify(cachedReportData?.compare_date) ===
                JSON.stringify(reportData.compare_date) &&
              JSON.stringify(cachedReportData?.report_filters) ===
                JSON.stringify(reportData.report_filters);
          }

          if (configMatches && reportDataMatches) {
            // Update timestamp when cache hit occurs
            void indexedDBCache.updateTimestamp(cacheKey);
            return data;
          }
        }
      } catch (error) {
        console.error("Error reading from cache:", error);
      }

      return null;
    },
    [getCacheKey],
  );

  const saveToCache = useCallback(
    async (
      accountId: string,
      reportId: string,
      sectionId: string,
      sectionConfiguration: CreativeSectionConfiguration,
      data: CreativeReportData,
      reportData?: CreativeReportMetadata,
    ) => {
      const cacheKey = getCacheKey(
        accountId,
        reportId,
        sectionId,
        sectionConfiguration,
        reportData,
      );

      try {
        // Check if we need to remove oldest entries before adding new one
        const count = await indexedDBCache.getCount();
        if (count >= MAX_CACHE_ENTRIES) {
          await indexedDBCache.removeOldestEntries();
        }

        // Use reportData if provided, otherwise derive from configuration for backward compatibility
        const selectedDate: CreativeReportMetadata = reportData || {
          date: sectionConfiguration.date,
          compare_date: sectionConfiguration.compare_date ?? null,
          report_filters: [],
        };

        const cacheEntry: CacheEntry = {
          data,
          timestamp: Date.now(),
          configuration: sectionConfiguration,
          selectedDate,
        };

        await indexedDBCache.set(cacheKey, cacheEntry);
      } catch (error) {
        console.error("Error writing to cache:", error);
      }
    },
    [getCacheKey],
  );

  const clearCacheForReport = useCallback(async (accountId: string, reportId: string) => {
    try {
      await indexedDBCache.clearForReport(accountId, reportId);
    } catch (error) {
      console.error("Error clearing cache:", error);
    }
  }, []);

  const clearCacheForAccount = useCallback(async (accountId: string) => {
    try {
      await indexedDBCache.clearForAccount(accountId);
    } catch (error) {
      console.error("Error clearing cache for account:", error);
    }
  }, []);

  const clearCacheByConfiguration = useCallback(
    async (
      accountId: string,
      configMatcher: (config: CreativeSectionConfiguration) => boolean,
    ) => {
      try {
        await indexedDBCache.clearByConfiguration(accountId, configMatcher);
      } catch (error) {
        console.error("Error clearing cache by configuration:", error);
      }
    },
    [],
  );

  const clearCacheByMetricId = useCallback(
    async (accountId: string, metricId: string) => {
      try {
        await indexedDBCache.clearByConfiguration(accountId, (config) => {
          // Check if the metric ID is present in the column_choices.metric array
          const metricIds = config.column_choices?.metric;
          if (Array.isArray(metricIds)) {
            return metricIds.includes(metricId);
          }
          return false;
        });
      } catch (error) {
        console.error("Error clearing cache by metric ID:", error);
      }
    },
    [],
  );

  const clearCacheForSection = useCallback(
    async (
      accountId: string,
      reportId: string,
      sectionId: string,
      configuration: CreativeSectionConfiguration,
    ) => {
      const cacheKey = getCacheKey(accountId, reportId, sectionId, configuration);
      try {
        await indexedDBCache.delete(cacheKey);
      } catch (error) {
        console.error("Error clearing section cache:", error);
      }
    },
    [getCacheKey],
  );

  const updateCacheConfiguration = useCallback(
    async (
      accountId: string,
      reportId: string,
      sectionId: string,
      newConfiguration: CreativeSectionConfiguration,
    ) => {
      const cacheKey = getCacheKey(accountId, reportId, sectionId, newConfiguration);
      try {
        const cachedData = await indexedDBCache.get(cacheKey);
        if (cachedData) {
          // Derive selectedDate from new configuration for backward compatibility
          const selectedDate: CreativeReportMetadata = {
            date: newConfiguration.date,
            compare_date: newConfiguration.compare_date ?? null,
            report_filters: [],
          };

          const updatedEntry: CacheEntry = {
            ...cachedData,
            configuration: newConfiguration,
            selectedDate,
            timestamp: Date.now(),
          };
          await indexedDBCache.set(cacheKey, updatedEntry);
        }
      } catch (error) {
        console.error("Error updating cache configuration:", error);
      }
    },
    [getCacheKey],
  );

  return {
    checkCache,
    saveToCache,
    clearCacheForAccount,
    clearCacheForReport,
    clearCacheByConfiguration,
    clearCacheByMetricId,
    clearCacheForSection,
    updateCacheConfiguration,
  };
};
